export class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, code, message, details) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
        this.name = 'ApiError';
    }
    /** True when the caller is authenticated but lacks the permission. */
    get isForbidden() {
        return this.status === 403;
    }
    /** Field-level messages from a validation failure, ready to show on a form. */
    get fieldErrors() {
        if (!Array.isArray(this.details))
            return {};
        return Object.fromEntries(this.details
            .filter((entry) => entry.path && entry.message)
            .map((entry) => [entry.path, entry.message]));
    }
}
export class ApiClient {
    options;
    refreshing = null;
    constructor(options) {
        this.options = options;
    }
    get baseUrl() {
        return this.options.baseUrl;
    }
    async request(path, options = {}) {
        const response = await this.send(path, options);
        // One refresh attempt, then give up — a loop here would hammer the API
        // with an expired session.
        if (response.status === 401 && !options.anonymous) {
            const refreshed = await this.refreshOnce();
            if (refreshed)
                return this.parse(await this.send(path, options));
            this.options.onUnauthenticated?.();
        }
        return this.parse(response);
    }
    get(path, query, signal) {
        return this.request(path, { method: 'GET', query, signal });
    }
    post(path, body, query) {
        return this.request(path, { method: 'POST', body, query });
    }
    patch(path, body) {
        return this.request(path, { method: 'PATCH', body });
    }
    put(path, body) {
        return this.request(path, { method: 'PUT', body });
    }
    delete(path) {
        return this.request(path, { method: 'DELETE' });
    }
    upload(path, formData) {
        return this.request(path, { method: 'POST', formData });
    }
    /** Fetches a binary response (a bank file, an Excel export). */
    async download(path, query) {
        const response = await this.send(path, { method: 'GET', query });
        if (response.status === 401) {
            if (await this.refreshOnce())
                return this.download(path, query);
            this.options.onUnauthenticated?.();
        }
        if (!response.ok)
            await this.parse(response);
        return response.blob();
    }
    async login(email, password) {
        const result = await this.request('/auth/login', {
            method: 'POST',
            body: { email, password },
            anonymous: true,
        });
        await this.options.tokens.setTokens(result);
        return result;
    }
    /**
     * Stores tokens obtained from an endpoint other than login — currently
     * invitation acceptance, which returns a full session.
     */
    async adoptSession(tokens) {
        await this.options.tokens.setTokens(tokens);
    }
    async logout() {
        const refreshToken = await this.options.tokens.getRefreshToken();
        try {
            await this.request('/auth/logout', { method: 'POST', body: { refreshToken } });
        }
        finally {
            await this.options.tokens.setTokens(null);
        }
    }
    async send(path, options) {
        // The query string is assembled by hand rather than through `new URL`,
        // because the web app's base is the relative '/api' that the dev server
        // proxies, and URL cannot parse a relative address without an origin.
        const query = new URLSearchParams();
        for (const [key, value] of Object.entries(options.query ?? {})) {
            if (value === undefined || value === null || value === '')
                continue;
            if (Array.isArray(value)) {
                for (const entry of value)
                    query.append(key, String(entry));
            }
            else {
                query.set(key, String(value));
            }
        }
        const search = query.toString();
        const url = `${this.options.baseUrl}${path}${search ? `?${search}` : ''}`;
        const headers = {};
        if (!options.anonymous) {
            const token = await this.options.tokens.getAccessToken();
            if (token)
                headers.authorization = `Bearer ${token}`;
        }
        // Never set Content-Type for FormData — the browser must add the boundary.
        if (options.body !== undefined && !options.formData)
            headers['content-type'] = 'application/json';
        return fetch(url, {
            method: options.method ?? 'GET',
            headers,
            body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
            signal: options.signal,
        });
    }
    /** Deduplicates concurrent refreshes so a burst of 401s makes one call. */
    refreshOnce() {
        this.refreshing ??= this.doRefresh().finally(() => {
            this.refreshing = null;
        });
        return this.refreshing;
    }
    async doRefresh() {
        const refreshToken = await this.options.tokens.getRefreshToken();
        if (!refreshToken)
            return false;
        try {
            const response = await this.send('/auth/refresh', {
                method: 'POST',
                body: { refreshToken },
                anonymous: true,
            });
            if (!response.ok) {
                await this.options.tokens.setTokens(null);
                return false;
            }
            await this.options.tokens.setTokens((await response.json()));
            return true;
        }
        catch {
            return false;
        }
    }
    async parse(response) {
        if (response.status === 204)
            return undefined;
        const text = await response.text();
        const payload = text ? safeJson(text) : null;
        if (!response.ok) {
            const error = payload?.error;
            throw new ApiError(response.status, error?.code ?? 'UNKNOWN', error?.message ?? `Request failed with ${response.status}`, error?.details);
        }
        return payload;
    }
}
function safeJson(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return { raw: text };
    }
}
//# sourceMappingURL=client.js.map